/*
  Vibe Recently Viewed — WonderSkull custom (not part of stock Shrine PRO).

  Fills the section's empty list from the visitor's own history:

  1. Read handles from localStorage (written by vibe-recently-viewed-track).
  2. Drop the product currently being viewed.
  3. Fetch ONE card per handle through the Section Rendering API and append in
     history order — newest first. Per-product beats a single search query
     here because Shopify's search index lags behind freshly published
     products, which are exactly the ones a visitor just opened. A 404 (product
     deleted or unpublished since it was viewed) is skipped silently.
  4. Reveal the section only once enough cards survived; below the merchant's
     minimum it stays hidden, so a first-time visitor sees nothing at all
     rather than an empty heading.

  Only "Products to show" cards are fetched up front. Anything deeper in the
  history waits behind See More and costs nothing unless the visitor asks for
  it — each card is ~4KB over the wire, so a batch is cheap but not free.
  See More is ours to drive: vibe-product-list.js reveals server-rendered
  overflow cards, and this section has none, so its marker class is off the
  wrapper and the click lands here instead.

  Nothing is requested until the section nears the viewport — off-screen it
  costs zero network. The section renders hidden and is revealed in one step,
  so it never expands mid-scroll and never contributes layout shift.

  The observer watches a 1px anchor rather than the section itself: the section
  starts hidden, and a display:none element never reports an intersection, so
  observing it directly would deadlock — it could only be filled once visible,
  and it only becomes visible once filled.
*/
(function () {
  var KEY = 'wsRecentlyViewed';
  var CARD_SECTION = 'vibe-recently-viewed-card';
  var MOBILE = window.matchMedia('(max-width: 749px)');

  function history() {
    try {
      var list = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(list) ? list.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) {
      return [];
    }
  }

  function fetchCard(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '?section_id=' + CARD_SECTION)
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html || html.indexOf('curated-products__item') === -1) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('[data-vrv-card]');
      })
      .catch(function () { return null; });
  }

  // block-curated-products.js measured this list at page load, when it was still empty
  // and display:none — so it flagged both ends reached and hid both arrows. It
  // recomputes on resize, and binds card images to recompute on load, but only the ones
  // present at init. Cards arriving later are covered by neither.
  function nudgeArrows(cards) {
    var fire = function () { window.dispatchEvent(new Event('resize')); };
    fire();
    cards.forEach(function (card) {
      card.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', fire, { once: true });
      });
    });
  }

  // How many to add per See More press: one screenful, matching the carousel's own
  // paging so a press advances by what the visitor can actually see.
  function batchSize(root) {
    var d = parseFloat(root.getAttribute('data-cp-step-desktop')) || 4;
    var m = parseFloat(root.getAttribute('data-cp-step-mobile')) || 2;
    return Math.max(1, Math.round(MOBILE.matches ? m : d));
  }

  // In-button loading state. Both See More affordances change size with their content
  // — the carousel tile is a full card-height button — so the box is frozen before the
  // label goes, otherwise the row collapses around the spinner and the grid reflows
  // under the visitor's cursor. Returns the undo.
  function showSpinner(btn) {
    var label = btn.innerHTML;
    var rect = btn.getBoundingClientRect();
    btn.style.boxSizing = 'border-box'; // so the frozen size includes existing padding
    btn.style.width = rect.width + 'px';
    btn.style.height = rect.height + 'px';

    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    btn.innerHTML = still
      ? '…'
      : '<svg class="vrv-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9" opacity="0.25"/><path d="M21 12a9 9 0 0 0-9-9"/></svg>';

    return function restore() {
      btn.innerHTML = label;
      btn.style.width = '';
      btn.style.height = '';
      btn.style.boxSizing = '';
    };
  }

  function setMoreVisible(root, visible) {
    root.querySelectorAll('[data-vrv-more]').forEach(function (host) {
      if (visible) host.removeAttribute('hidden');
      else host.setAttribute('hidden', '');
    });
  }

  // Appends the next `count` handles. Dead handles are skipped without consuming the
  // batch, so a press always adds a full screenful when the history can still supply
  // one. Resolves with how many cards landed.
  function appendNext(state, count) {
    var take = state.queue.splice(0, count);
    if (!take.length) return Promise.resolve(0);

    return Promise.all(take.map(fetchCard)).then(function (cards) {
      var kept = cards.filter(Boolean);
      // Append in request order so the list stays newest-viewed first, however the
      // responses come back.
      kept.forEach(function (card) { state.list.appendChild(card); });
      var shortfall = take.length - kept.length;
      if (shortfall > 0 && state.queue.length) return appendNext(state, shortfall).then(function (n) { return kept.length + n; });
      return kept.length;
    });
  }

  function wireSeeMore(state) {
    state.root.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-cp-load-more]');
      if (!btn || !state.root.contains(btn) || state.busy) return;
      event.preventDefault();

      state.busy = true;
      btn.setAttribute('aria-busy', 'true');
      var restore = showSpinner(btn);
      var before = state.list.children.length;

      appendNext(state, batchSize(state.root)).then(function () {
        state.busy = false;
        btn.removeAttribute('aria-busy');
        // Restore before deciding visibility: the button often survives for the next
        // press, and when it doesn't it should still be whole if anything reveals it.
        restore();
        setMoreVisible(state.root, state.queue.length > 0);

        var added = [].slice.call(state.list.children, before);
        nudgeArrows(added);

        // Carousel: bring the first newly added card into view, mirroring what the
        // grid does for free by simply growing taller.
        var last = added[added.length - 1];
        if (last && state.list.scrollWidth > state.list.clientWidth) {
          last.scrollIntoView({
            inline: 'end',
            block: 'nearest',
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        }
      });
    });
  }

  function fill(root) {
    if (root.__vrvDone) return;
    root.__vrvDone = true;

    var list = root.querySelector('[data-vrv-list]');
    if (!list) return;
    var limit = parseInt(list.getAttribute('data-vrv-limit'), 10) || 4;
    var min = parseInt(list.getAttribute('data-vrv-min'), 10) || 1;
    var current = list.getAttribute('data-vrv-current');

    var state = {
      root: root,
      list: list,
      busy: false,
      queue: history().filter(function (h) { return h !== current; })
    };
    if (!state.queue.length) return; // first-time visitor: leave the section hidden

    appendNext(state, limit).then(function (kept) {
      if (kept < min) return;
      root.removeAttribute('hidden');
      setMoreVisible(root, state.queue.length > 0);
      wireSeeMore(state);
      nudgeArrows([].slice.call(list.children));
      root.dispatchEvent(new CustomEvent('vrv:filled', { bubbles: true, detail: { count: kept } }));
    });
  }

  function rootFor(anchor) {
    var id = anchor.getAttribute('data-vrv-anchor');
    return document.querySelector('[data-vrv-root][data-vrv-id="' + id + '"]');
  }

  function watch(anchor) {
    var root = rootFor(anchor);
    if (!root) return;
    // Skip the observer entirely when there is nothing to show: a first-time
    // visitor should cost no work at all.
    if (!history().length) return;
    if (!('IntersectionObserver' in window)) { fill(root); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        fill(root);
      });
    }, { rootMargin: '400px 0px' }); // start early enough that cards are there before it scrolls in
    io.observe(anchor);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-vrv-anchor]').forEach(watch);
  }

  initAll();
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
})();
